import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, X, Loader2, Check, Sparkles, Printer, Trash2, ArrowLeft, AlertTriangle, Megaphone, ClipboardList } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { PlaybookStep, VendorEvaluation, VendorEntry, VendorResearch } from '../../types/aai';
import { useParticipant } from '../../hooks/useParticipant';
import { Loading, ErrorNote } from './components';
import { PageHeader } from './dashboards/ui';

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200';

// The comparison dimensions (rows), in the order the doc lays out per-vendor.
// `hl` marks the row the design highlights — data flow is where evaluations die.
const DIMS: { key: keyof VendorEntry; label: string; hl?: boolean }[] = [
  { key: 'what_it_does', label: 'What it does' },
  { key: 'technical_fit', label: 'Technical fit' },
  { key: 'pricing_tier', label: 'Pricing tier' },
  { key: 'cost_analysis', label: 'Cost / financial' },
  { key: 'soc2', label: 'SOC 2' },
  { key: 'data_retention', label: 'Data retention' },
  { key: 'data_flow', label: 'Data flow · sub-processors · residency', hl: true },
  { key: 'eu_ai_act_class', label: 'EU AI Act class' },
  { key: 'controls', label: 'Controls satisfied' },
  { key: 'alternatives', label: 'Alternatives' },
  { key: 'overlap_note', label: 'Overlap / shelfware' },
];

// Starting checklist for a fresh playbook — a template the champion edits, not data.
const DEFAULT_PLAYBOOK: PlaybookStep[] = [
  { text: 'Create the workspace & verify the email domain', done: false },
  { text: 'Configure SSO', done: false },
  { text: 'Set data retention — disable training on your data', done: false },
  { text: 'Define workspace roles & permissions', done: false },
  { text: 'Enable audit-log export', done: false },
  { text: 'Pilot-group rollout + collect feedback', done: false },
];

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function printEvaluation(ev: VendorEvaluation): void {
  const win = window.open('', '_blank', 'width=980,height=1000');
  if (!win) return;
  const vendors = ev.vendors || [];
  const head = vendors.map((v) => `<th>${esc(v.name)}${v.rating ? ` · ${v.rating}/5` : ''}</th>`).join('');
  const rows = DIMS.map((d) =>
    `<tr><th class="dim">${esc(d.label)}</th>${vendors.map((v) => `<td>${esc(String(v[d.key] ?? '—'))}</td>`).join('')}</tr>`,
  ).join('');
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(ev.title)} — vendor evaluation</title>
  <style>
    body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #1a1a2e; margin: 36px; }
    h1 { font-size: 21px; margin: 0 0 2px; } .sub { color: #667; font-size: 12px; margin-bottom: 18px; }
    table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
    th, td { border: 1px solid #e6e9f2; padding: 7px 9px; text-align: left; vertical-align: top; }
    thead th { background: #EEF3FF; } th.dim { background: #fafbff; width: 150px; color: #445; font-weight: 600; }
    .rec { margin-top: 18px; padding: 14px 16px; background: #EEF3FF; border-radius: 10px; font-size: 12.5px; }
    .foot { margin-top: 20px; font-size: 10.5px; color: #99a; }
    @media print { body { margin: 18px; } thead th, th.dim { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body>
    <h1>${esc(ev.title)}</h1>
    <div class="sub">Vendor evaluation${ev.framework_note ? ` · ${esc(ev.framework_note)}` : ''} · ${ev.status}</div>
    <table><thead><tr><th class="dim">Dimension</th>${head}</tr></thead><tbody>${rows}</tbody></table>
    ${ev.recommendation ? `<div class="rec"><b>Recommendation:</b> ${esc(ev.recommendation)}</div>` : ''}
    <div class="foot">Research-only evaluation. Verify vendor data (pricing, SOC 2, data flow) against the vendor's current terms before any decision.</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},200);};</script>
  </body></html>`);
  win.document.close();
}

const VendorEvaluatorPage: React.FC = () => {
  const { context, ready } = useParticipant();
  const canAct = !!context?.capabilities.manage_program;
  const [params, setParams] = useSearchParams();

  const [list, setList] = useState<VendorEvaluation[]>([]);
  const [selected, setSelected] = useState<string | null>(params.get('open'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  // ?evaluate=<tool> — the registry drawer's "closes the loop" entry point.
  const [pendingTool, setPendingTool] = useState<string | null>(params.get('evaluate'));

  useEffect(() => {
    if (!pendingTool) return;
    setCreating(true);
    setNewTitle(`${pendingTool}: evaluation`);
  }, [pendingTool]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { setList((await aaiApi.listVendorEvaluations()).data.evaluations); setError(null); }
    catch { setError('Could not load evaluations.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (!selected) loadList(); }, [selected, loadList]);

  const create = async () => {
    if (!newTitle.trim()) return;
    const { data } = await aaiApi.createVendorEvaluation({ title: newTitle.trim() });
    setNewTitle(''); setCreating(false); setSelected(data.id);
  };

  const closeDetail = () => {
    setSelected(null); setPendingTool(null);
    if (params.get('open') || params.get('evaluate')) setParams({}, { replace: true });
  };

  if (!ready) return <Loading />;

  if (selected) return <EvalDetail id={selected} canAct={canAct} seedTool={pendingTool} onSeeded={() => setPendingTool(null)} onBack={closeDetail} />;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Which tool should we use, and why?"
        subtitle="Comparative, decision-oriented analysis — fit, cost, data flow, and compliance side by side. AI drafts the first pass; a human verifies. Then deploy it with a playbook and drive adoption."
      />

      {canAct && (
        <div className="flex justify-end mb-3">
          {creating ? (
            <div className="flex items-center gap-2">
              <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                placeholder="e.g. Chat assistant: Claude vs ChatGPT vs Copilot" className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] w-80 focus:outline-none focus:ring-2 focus:ring-brand-200" />
              <button onClick={create} disabled={!newTitle.trim()} className="text-[12.5px] font-semibold bg-brand-500 text-white px-3 py-2 rounded-lg disabled:opacity-50">Create</button>
              <button onClick={() => { setCreating(false); setPendingTool(null); }} className="text-[12px] text-gray-500">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-2 whitespace-nowrap"><Plus size={14} /> New evaluation</button>
          )}
        </div>
      )}

      {loading ? <Loading /> : error ? <ErrorNote message={error} /> : list.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-8 text-center text-[13px] text-gray-400">No evaluations yet — start one to compare tools.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {list.map((e) => (
            <button key={e.id} onClick={() => setSelected(e.id)} className="text-left bg-white border border-gray-100 rounded-xl p-4 hover:border-brand-200">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="font-semibold text-[14px] leading-snug">{e.title}</span>
                <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${e.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{e.status}</span>
              </div>
              {e.framework_note && <div className="text-[12px] text-gray-500 mb-2">{e.framework_note}</div>}
              {(e.tools || []).length > 0 ? (
                <div className="flex gap-1.5 flex-wrap">
                  {(e.tools || []).map((t) => (
                    <span key={t} className="bg-gray-100 text-gray-600 rounded-full px-2.5 py-0.5 text-[11px] font-medium">{t}</span>
                  ))}
                </div>
              ) : (
                <div className="text-[12px] text-gray-400">No tools compared yet</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── evaluation detail ──────────────────────────────────────────────────

const EvalDetail: React.FC<{ id: string; canAct: boolean; seedTool: string | null; onSeeded: () => void; onBack: () => void }> = ({ id, canAct, seedTool, onSeeded, onBack }) => {
  const [ev, setEv] = useState<VendorEvaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<VendorEntry | null>(null);
  const [rec, setRec] = useState('');
  const [recSaved, setRecSaved] = useState(false);
  const [announcing, setAnnouncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await aaiApi.getVendorEvaluation(id); setEv(data); setRec(data.recommendation || ''); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Landed here from the registry's "Evaluate <tool>" — open the add-tool modal
  // with the tool name already filled, once.
  useEffect(() => {
    if (seedTool && ev && canAct && !loading) { setAdding(true); }
  }, [seedTool, ev, canAct, loading]);

  const saveRec = async () => { await aaiApi.updateVendorEvaluation(id, { recommendation: rec }); setRecSaved(true); setTimeout(() => setRecSaved(false), 2000); };
  const togglePublish = async () => { if (!ev) return; await aaiApi.publishVendorEvaluation(id, ev.status !== 'published'); load(); };
  const removeVendor = async (vid: string) => { if (window.confirm('Remove this tool from the comparison?')) { await aaiApi.deleteVendor(vid); load(); } };
  const removeEval = async () => { if (window.confirm('Delete this whole evaluation?')) { await aaiApi.deleteVendorEvaluation(id); onBack(); } };

  const savePlaybook = async (steps: PlaybookStep[]) => {
    const { data } = await aaiApi.setEvaluationPlaybook(id, steps);
    setEv((p) => (p ? { ...p, playbook: data.playbook } : p));
  };
  const announce = async () => {
    setAnnouncing(true);
    try { const { data } = await aaiApi.announceEvaluation(id); setEv((p) => (p ? { ...p, ...data, vendors: p.vendors } : p)); }
    finally { setAnnouncing(false); }
  };

  if (loading || !ev) return <Loading label="Loading evaluation…" />;
  const vendors = ev.vendors || [];
  const published = ev.status === 'published';

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-gray-500 hover:text-gray-700 mb-3"><ArrowLeft size={14} /> All evaluations</button>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{ev.title}</h1>
          {ev.framework_note && <p className="text-[12.5px] text-gray-500 mt-0.5">{ev.framework_note}</p>}
        </div>
        {canAct && (
          <div className="flex items-center gap-2">
            <button onClick={togglePublish}
              className={`text-[12px] font-semibold border rounded-lg px-3 py-1.5 ${published ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-500 hover:border-brand-300'}`}>
              {published ? 'Unpublish' : 'Publish'}
            </button>
            <button onClick={() => printEvaluation(ev)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-1.5 whitespace-nowrap"><Printer size={14} /> PDF</button>
            <button onClick={removeEval} title="Delete" className="text-gray-300 hover:text-red-500"><Trash2 size={15} /></button>
          </div>
        )}
      </div>

      {vendors.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-8 text-center text-[13px] text-gray-400 mb-4">No tools yet — add 2 or more to compare.</div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden mb-4">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] align-top">
              <thead><tr className="bg-gray-50/70">
                <th className="px-3 py-2.5 text-left font-sans text-[10px] uppercase tracking-wider text-gray-400 w-44">Dimension</th>
                {vendors.map((v) => (
                  <th key={v.id} className="px-3 py-2.5 text-left min-w-[180px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <span className="font-semibold text-[13px]">{v.name}</span>
                        {v.rating ? <span className="text-[11px] font-bold text-brand-600 bg-brand-50 rounded px-1.5 py-0.5">{v.rating}/5</span> : null}
                      </span>
                      {canAct && (
                        <span className="flex items-center gap-1.5">
                          <button onClick={() => setEditing(v)} className="text-[11px] font-semibold text-gray-400 hover:text-brand-600">edit</button>
                          <button onClick={() => removeVendor(v.id)} className="text-gray-300 hover:text-red-500"><X size={13} /></button>
                        </span>
                      )}
                    </div>
                    {v.prefilled && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 mt-1"><AlertTriangle size={10} /> pre-filled — verify</span>}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {DIMS.map((d) => (
                  <tr key={d.key} className={`border-t border-gray-50 ${d.hl ? 'bg-brand-50/40' : ''}`}>
                    <th className="px-3 py-2.5 text-left font-semibold text-gray-500 text-[11.5px] align-top">{d.label}</th>
                    {vendors.map((v) => <td key={v.id} className="px-3 py-2.5 text-gray-700 align-top">{(v[d.key] as string) || <span className="text-gray-300">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canAct && (
        <div className="flex items-center gap-2 mb-5">
          <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand-600 border border-brand-200 rounded-lg px-3 py-2 whitespace-nowrap"><Plus size={14} /> Add a tool</button>
        </div>
      )}

      {/* recommendation — the documented, defensible verdict */}
      <div className="bg-brand-50 rounded-xl p-5 mb-4">
        <div className="text-[11px] font-sans uppercase tracking-wide text-gray-400 mb-2">Recommendation</div>
        {canAct ? (
          <>
            <textarea value={rec} onChange={(e) => setRec(e.target.value)} rows={3} placeholder="The documented, defensible recommendation for leadership…" className={inputCls} />
            <div className="flex items-center gap-3 mt-2">
              <button onClick={saveRec} className="text-[12px] font-semibold bg-brand-500 text-white px-3 py-1.5 rounded-lg">Save recommendation</button>
              {recSaved && <span className="text-[12px] text-emerald-600 font-semibold">Saved.</span>}
            </div>
          </>
        ) : ev.recommendation ? (
          <p className="text-[13.5px] text-gray-800 leading-relaxed m-0">{ev.recommendation}</p>
        ) : (
          <p className="text-[13px] text-gray-400 italic m-0">The documented, defensible recommendation for leadership goes here.</p>
        )}
      </div>

      <PlaybookCard steps={ev.playbook || []} canAct={canAct} onSave={savePlaybook} />

      {/* drive adoption — connect the verdict to usage */}
      {published && (
        <div className="bg-black text-white rounded-xl p-5 mb-4">
          <div className="flex items-center gap-3.5 flex-wrap">
            <span className="shrink-0 w-10 h-10 rounded-xl bg-white/10 text-accent-500 grid place-items-center"><Megaphone size={19} /></span>
            <div className="flex-1 min-w-[220px]">
              <div className="font-bold text-[14px] mb-0.5">Drive adoption</div>
              <p className="text-[12.5px] text-white/60 m-0 leading-relaxed">
                {ev.announced_at
                  ? "The company was notified. The verdict now appears on the What's Allowed page with its approved use."
                  : "Publishing recorded the verdict. Now connect it to usage — announce the approved tool and it lands on What's Allowed."}
              </p>
            </div>
            {ev.announced_at ? (
              <span className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-400/20 text-emerald-300 rounded-full px-3.5 py-2 text-[12.5px] font-semibold"><Check size={14} /> Announced · live on What's Allowed</span>
            ) : canAct ? (
              <button onClick={announce} disabled={announcing}
                className="shrink-0 inline-flex items-center gap-1.5 bg-accent-500 text-black rounded-lg px-4 py-2.5 text-[13px] font-bold disabled:opacity-60 whitespace-nowrap">
                {announcing && <Loader2 size={14} className="animate-spin" />} Announce to company
              </button>
            ) : null}
          </div>
        </div>
      )}

      {(adding || editing) && (
        <VendorModal evalId={id} vendor={editing} seedName={adding ? seedTool : null}
          onClose={() => { setAdding(false); setEditing(null); onSeeded(); }}
          onSaved={() => { setAdding(false); setEditing(null); onSeeded(); load(); }} />
      )}
    </div>
  );
};

// ── deployment playbook (reusable CoE asset) ─────────────────────────────

const PlaybookCard: React.FC<{ steps: PlaybookStep[]; canAct: boolean; onSave: (steps: PlaybookStep[]) => Promise<void> }> = ({ steps, canAct, onSave }) => {
  const [newStep, setNewStep] = useState('');
  const [busy, setBusy] = useState(false);
  const done = steps.filter((s) => s.done).length;

  const mutate = async (next: PlaybookStep[]) => {
    setBusy(true);
    try { await onSave(next); } finally { setBusy(false); }
  };
  const toggle = (i: number) => mutate(steps.map((s, j) => (j === i ? { ...s, done: !s.done } : s)));
  const remove = (i: number) => mutate(steps.filter((_, j) => j !== i));
  const add = () => {
    if (!newStep.trim()) return;
    mutate([...steps, { text: newStep.trim(), done: false }]);
    setNewStep('');
  };

  if (steps.length === 0 && !canAct) return null;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="text-[11px] font-sans uppercase tracking-wide font-semibold text-purple-600 inline-flex items-center gap-1.5"><ClipboardList size={13} /> Deployment playbook · reusable CoE asset</div>
        {steps.length > 0 && <span className="text-[12px] font-semibold text-gray-500">{done} / {steps.length} done</span>}
      </div>
      <p className="text-[12.5px] text-gray-500 mb-3.5 leading-relaxed">Verified once, run every time. A champion runs the checklist after the verdict — setup, not selection.</p>
      {steps.length === 0 ? (
        <button onClick={() => mutate(DEFAULT_PLAYBOOK)} disabled={busy}
          className="text-[12.5px] font-semibold text-purple-600 border border-purple-200 rounded-lg px-3 py-2 disabled:opacity-50">
          Start from the standard rollout checklist
        </button>
      ) : (
        <div className="space-y-0.5">
          {steps.map((s, i) => (
            <div key={i} className={`group flex items-center gap-3 px-3 py-2 rounded-lg ${s.done ? 'bg-emerald-50/60' : ''} ${canAct ? 'cursor-pointer' : ''}`}
              onClick={() => { if (canAct && !busy) toggle(i); }}>
              <span className={`shrink-0 w-5 h-5 rounded-md grid place-items-center border ${s.done ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-gray-300'}`}>
                {s.done && <Check size={12} className="text-white" strokeWidth={3} />}
              </span>
              <span className={`flex-1 text-[13px] ${s.done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{s.text}</span>
              {canAct && (
                <button onClick={(e) => { e.stopPropagation(); if (!busy) remove(i); }}
                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500"><X size={13} /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {canAct && steps.length > 0 && (
        <div className="flex items-center gap-2 mt-3">
          <input value={newStep} onChange={(e) => setNewStep(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="Add a step…" className={`${inputCls} max-w-sm`} />
          <button onClick={add} disabled={busy || !newStep.trim()} className="text-[12.5px] font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-2 disabled:opacity-50">Add</button>
        </div>
      )}
    </div>
  );
};

// ── add/edit vendor modal with AI research prefill ──────────────────────

const VendorModal: React.FC<{ evalId: string; vendor: VendorEntry | null; seedName?: string | null; onClose: () => void; onSaved: () => void }> = ({ evalId, vendor, seedName, onClose, onSaved }) => {
  const blank = { name: seedName || '', what_it_does: '', technical_fit: '', cost_analysis: '', pricing_tier: '', soc2: '', data_retention: '', data_flow: '', eu_ai_act_class: '', controls: '', alternatives: '', overlap_note: '', rating: '' };
  const [f, setF] = useState<Record<string, string>>(vendor ? {
    name: vendor.name, what_it_does: vendor.what_it_does || '', technical_fit: vendor.technical_fit || '',
    cost_analysis: vendor.cost_analysis || '', pricing_tier: vendor.pricing_tier || '', soc2: vendor.soc2 || '',
    data_retention: vendor.data_retention || '', data_flow: vendor.data_flow || '', eu_ai_act_class: vendor.eu_ai_act_class || '',
    controls: vendor.controls || '', alternatives: vendor.alternatives || '', overlap_note: vendor.overlap_note || '',
    rating: vendor.rating ? String(vendor.rating) : '',
  } : blank);
  const [prefilled, setPrefilled] = useState(vendor?.prefilled || false);
  const [researching, setResearching] = useState(false);
  const [researchNote, setResearchNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const research = async () => {
    if (!f.name.trim()) { setErr('Enter a tool name to research.'); return; }
    setResearching(true); setErr(null);
    try {
      const { data }: { data: VendorResearch } = await aaiApi.researchVendor(evalId, f.name.trim());
      const rf = data.fields || {};
      setF((p) => ({
        ...p,
        what_it_does: (rf.what_it_does as string) || p.what_it_does,
        technical_fit: (rf.technical_fit as string) || p.technical_fit,
        cost_analysis: (rf.cost_analysis as string) || p.cost_analysis,
        pricing_tier: (rf.pricing_tier as string) || p.pricing_tier,
        soc2: (rf.soc2 as string) || p.soc2,
        data_retention: (rf.data_retention as string) || p.data_retention,
        data_flow: (rf.data_flow as string) || p.data_flow,
        eu_ai_act_class: (rf.eu_ai_act_class as string) || p.eu_ai_act_class,
        controls: (rf.controls as string) || p.controls,
        alternatives: (rf.alternatives as string) || p.alternatives,
        overlap_note: data.overlap_note || p.overlap_note,
        rating: rf.rating ? String(rf.rating) : p.rating,
      }));
      setPrefilled(data.prefilled);
      setResearchNote(data.note);
    } catch { setErr('Research failed — fill the fields in manually.'); }
    finally { setResearching(false); }
  };

  const save = async () => {
    if (!f.name.trim()) { setErr('A tool name is required.'); return; }
    setBusy(true); setErr(null);
    const body: Record<string, unknown> = { ...f, rating: f.rating ? Number(f.rating) : undefined };
    Object.keys(body).forEach((k) => { if (body[k] === '') body[k] = undefined; });
    try {
      if (vendor) await aaiApi.updateVendor(vendor.id, body);
      else await aaiApi.addVendor(evalId, { ...(body as { name: string }), prefilled });
      onSaved();
    } catch (e) { setErr((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Could not save.'); setBusy(false); }
  };

  const F = (k: string, label: string, area = false) => (
    <label className="block mb-2.5"><span className="block text-[11px] font-semibold text-gray-600 mb-1">{label}</span>
      {area ? <textarea value={f[k]} onChange={(e) => set(k, e.target.value)} rows={2} className={inputCls} />
        : <input value={f[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} />}
    </label>
  );

  return (
    <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="text-base font-semibold text-gray-900">{vendor ? 'Edit tool' : 'Add a tool'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="flex items-end gap-2 mb-3">
          <label className="block flex-1"><span className="block text-[11px] font-semibold text-gray-600 mb-1">Tool / vendor name</span>
            <input autoFocus value={f.name} onChange={(e) => set('name', e.target.value)} className={inputCls} /></label>
          {!vendor && (
            <button onClick={research} disabled={researching || !f.name.trim()} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-purple-500 hover:bg-purple-600 rounded-lg px-3 py-2 disabled:opacity-50 whitespace-nowrap">
              {researching ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Research with AI
            </button>
          )}
        </div>
        {researchNote && <p className="text-[11.5px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5"><AlertTriangle size={13} /> {researchNote}</p>}

        {F('what_it_does', 'What it does', true)}
        {F('technical_fit', 'Technical fit', true)}
        <div className="grid grid-cols-2 gap-2.5">
          {F('pricing_tier', 'Pricing tier')}
          <label className="block mb-2.5"><span className="block text-[11px] font-semibold text-gray-600 mb-1">SOC 2</span>
            <select value={f.soc2} onChange={(e) => set('soc2', e.target.value)} className={inputCls}>
              <option value="">—</option><option value="yes">Yes</option><option value="no">No</option><option value="in_progress">In progress</option><option value="unknown">Unknown</option>
            </select></label>
        </div>
        {F('cost_analysis', 'Cost / financial analysis', true)}
        {F('data_flow', 'Data flow · sub-processors · residency', true)}
        {F('data_retention', 'Data retention', true)}
        <div className="grid grid-cols-2 gap-2.5">
          <label className="block mb-2.5"><span className="block text-[11px] font-semibold text-gray-600 mb-1">EU AI Act class</span>
            <select value={f.eu_ai_act_class} onChange={(e) => set('eu_ai_act_class', e.target.value)} className={inputCls}>
              <option value="">—</option>{['prohibited', 'high', 'limited', 'minimal', 'unknown'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="block mb-2.5"><span className="block text-[11px] font-semibold text-gray-600 mb-1">Rating (1–5)</span>
            <input type="number" min={1} max={5} value={f.rating} onChange={(e) => set('rating', e.target.value)} className={inputCls} /></label>
        </div>
        {F('controls', 'Controls satisfied', true)}
        {F('alternatives', 'Alternatives', true)}
        {F('overlap_note', 'Overlap / shelfware', true)}

        {err && <p className="text-[12px] text-red-600 mb-2">{err}</p>}
        <div className="flex justify-end gap-2 mt-1">
          <button onClick={onClose} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">Cancel</button>
          <button disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 text-white disabled:opacity-50 whitespace-nowrap">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save tool</button>
        </div>
      </div>
    </div>
  );
};

export default VendorEvaluatorPage;
